/**
 * RecordDetail - Expanded view for a single record
 *
 * Atomic level: Organism
 * Composes: DimensionLabelGroup, DetectionIndicator, Collapsible
 *
 * Layout (matching Figma design):
 * - Gray background with padding
 * - Top row: Left card (verbatim + thread) + Right card (business context)
 * - Conduct Labels card
 * - Detection card
 * - Investigation card
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import { DimensionLabelGroup, groupLabelsByCategory } from './DimensionLabelGroup';
import { OutcomePredictionWithConfidence } from './OutcomePredictionWithConfidence';
import { FiveHeadComplaintTest } from './FiveHeadComplaintTest';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { PastSimulationsTable } from './PastSimulationsTable';
import { OutcomeSimulationModal } from './OutcomeSimulationModal';
import { ProcessingStatusBadge } from './ProcessingStatusBadge';
import { ClassificationReviewModal } from './ClassificationReviewModal';
import { RecordEvidenceSection } from './RecordEvidenceSection';
import { formatTaxonomyLabel } from './taxonomyLabelFormatting';
import IconWrapper from '../../../ui/IconWrapper';
import { toast } from 'sonner';
import { useSimulations } from '../../../../hooks/useSimulations';
import type { OrganizationDisplaySettings } from '../../../../utils/api';
import {
  FiChevronDown,
  FiChevronRight,
  FiCheck,
  FiInfo,
  FiRefreshCw,
  FiMoreVertical,
  FiClipboard,
} from 'react-icons/fi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../ui/dropdown-menu';
import type { Record as ConductRecord, VulnerabilityIndicator, DynamicCategoryLabels, TaxonomyLabel } from '../../../../types/record';

interface RecordDetailProps {
  record: ConductRecord;
  complaintDetectionEnabled?: boolean;
  vulnerabilityDetectionEnabled?: boolean;
  organizationDisplaySettings?: OrganizationDisplaySettings | null;
  onConfirmComplaint?: (recordId: string) => void;
  onRejectComplaint?: (recordId: string) => void;
  onReclassify?: (recordId: string) => void;
  onStartSimulation?: (recordId: string) => void;
  isReclassifying?: boolean;
  isStartingSimulation?: boolean;
  className?: string;
}


/**
 * Format an ISO date string as a relative date or absolute date
 */
const formatRelativeDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Convert dynamic category labels to the groups format for DimensionLabelGroup.
 * Categories are displayed in the order they appear in the data.
 */
const categoriesToGroups = (categories: DynamicCategoryLabels): { name: string; labels: TaxonomyLabel[] }[] => {
  return Object.entries(categories).map(([categoryId, labels]) => {
    // Use categoryName from first label if available, otherwise format the categoryId
    const displayName = labels[0]?.categoryName || formatTaxonomyLabel(categoryId);
    return {
      name: displayName,
      labels,
    };
  });
};

const RecordDetail: React.FC<RecordDetailProps> = ({
  record,
  complaintDetectionEnabled = true,
  vulnerabilityDetectionEnabled = true,
  organizationDisplaySettings,
  onConfirmComplaint,
  onRejectComplaint,
  onReclassify,
  onStartSimulation,
  isReclassifying = false,
  isStartingSimulation = false,
  className,
}) => {
  const [showThread, setShowThread] = useState(false);
  const [showVerbatim, setShowVerbatim] = useState(false);
  const [complaintDetectionOpen, setComplaintDetectionOpen] = useState(true);
  const [vulnerabilityDetectionOpen, setVulnerabilityDetectionOpen] = useState(true);
  const [investigationOpen, setInvestigationOpen] = useState(true);
  const [pastSimulationsOpen, setPastSimulationsOpen] = useState(false);
  const [outcomeSimulationModalOpen, setOutcomeSimulationModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const rootCauseClassificationEnabled = organizationDisplaySettings?.root_cause_classification_enabled ?? true;

  // Fetch past simulations for this record
  const { simulations, isLoading: simulationsLoading } = useSimulations(record.id, {
    enabled: record.isConfirmedComplaint,
  });

  return (
    <div className={cn('w-full bg-card', className)}>
      <div className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.65fr)_320px]">
          <div className="min-w-0 space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <ConductScoreIndicator
                score={record.conductScore}
                size="medium"
                className="flex-shrink-0"
                breakdown={record.conductScoreBreakdown}
              />
              <div className="min-w-0 flex-1 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-base font-semibold text-foreground sm:text-lg">
                    {record.summary}
                  </h2>
                  <div className="flex shrink-0 items-center gap-2">
                    {(record.processing_status || record._progress?.stage) && (
                      <ProcessingStatusBadge record={record} />
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Customer:</span>
                    <span className="font-medium text-foreground">{record.customerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Date:</span>
                    <span className="font-medium text-foreground">
                      {new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {(record.summaryDetailed || record.verbatim) && (
              <div className="border-t border-border/70 pt-5">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {showVerbatim ? 'Original Text' : 'Analysis Summary'}
                  </h4>
                  {record.summaryDetailed && record.verbatim && (
                    <button
                      type="button"
                      onClick={() => setShowVerbatim(!showVerbatim)}
                      className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {showVerbatim ? 'Show Summary' : 'Show Original'}
                    </button>
                  )}
                </div>
                {showVerbatim && record.verbatim ? (
                  <div className="max-h-[500px] overflow-y-auto text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {record.verbatim}
                  </div>
                ) : record.summaryDetailed ? (
                  <div className="space-y-3">
                    {record.summaryDetailed.split('\n\n').map((paragraph, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {record.customerThread && record.customerThread.messages.length > 0 && (
              <div className="border-t border-border/70 pt-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Customer Communication</h4>
                    <p className="text-xs text-muted-foreground">
                      {record.customerThread.messageCount} {record.customerThread.messageCount === 1 ? 'message' : 'messages'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowThread(!showThread)}
                    className="h-6 px-2 text-xs text-primary transition-colors hover:text-primary/80 hover:underline"
                  >
                    {showThread ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                {showThread && (
                  <div className="flex flex-col gap-4">
                    {record.customerThread.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          'flex max-w-[85%] gap-3',
                          msg.isCustomer ? 'self-start' : 'self-end flex-row-reverse'
                        )}
                      >
                        <div
                          className={cn(
                            'flex size-9 flex-shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                            msg.isCustomer
                              ? 'border-primary/20 bg-primary/10 text-primary'
                              : 'border-border bg-muted text-muted-foreground'
                          )}
                        >
                          {msg.authorInitials}
                        </div>

                        <div
                          className={cn(
                            'flex min-w-0 flex-col gap-1',
                            msg.isCustomer ? 'items-start' : 'items-end'
                          )}
                        >
                          <div
                            className={cn(
                              'flex flex-wrap items-center gap-2',
                              msg.isCustomer ? 'flex-row' : 'flex-row-reverse'
                            )}
                          >
                            <span className="text-sm font-medium text-foreground">
                              {msg.author}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {msg.timestamp}
                            </span>
                          </div>
                          <div
                            className={cn(
                              'max-w-full rounded-2xl border px-4 py-3',
                              msg.isCustomer
                                ? 'rounded-tl-sm border-primary/10 bg-primary/5'
                                : 'rounded-tr-sm border-border bg-muted/60'
                            )}
                          >
                            <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {msg.content}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="space-y-4 border-t border-border/70 pt-5 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold text-foreground">
                Operational Context
              </h4>
              {onReclassify && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8">
                      <IconWrapper icon={FiMoreVertical} className="size-4" />
                      <span className="sr-only">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onReclassify(record.id)}
                      disabled={isReclassifying}
                    >
                      <IconWrapper
                        icon={FiRefreshCw}
                        className={cn('mr-2 size-4', isReclassifying && 'animate-spin')}
                      />
                      {isReclassifying ? 'Reclassifying...' : 'Reclassify with AI'}
                    </DropdownMenuItem>
                    {record.classificationMeta?.classificationId && (
                      <DropdownMenuItem onClick={() => setReviewModalOpen(true)}>
                        <IconWrapper icon={FiClipboard} className="mr-2 size-4" />
                        Review Classification
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="font-mono text-xs font-normal text-muted-foreground">
                      ID: {record.id}
                    </DropdownMenuLabel>
                    {record.classificationMeta && (
                      <>
                        {record.classificationMeta.taxonomyVersion && (
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Taxonomy v{record.classificationMeta.taxonomyVersion}
                          </DropdownMenuLabel>
                        )}
                        {record.classificationMeta.promptVersions && (
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            <div>
                              <span className="font-semibold">Prompt versions</span>
                              {Object.entries(record.classificationMeta.promptVersions).map(
                                ([dim, ver]) => (
                                  <div key={dim} className="pl-2">
                                    {dim.replace(/_/g, ' ')}: {ver.startsWith('v') ? ver : `v${ver}`}
                                  </div>
                                )
                              )}
                            </div>
                          </DropdownMenuLabel>
                        )}
                        {record.classificationMeta.classifiedAt && (
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Classified {formatRelativeDate(record.classificationMeta.classifiedAt)}
                          </DropdownMenuLabel>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="rounded-xl border border-border/70 bg-muted/25 p-4">
              <div className="grid gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Product</p>
                  <p className="text-sm font-medium text-foreground">
                    {record.operationalContext.product}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Journey Stage</p>
                  <p className="text-sm font-medium text-foreground">
                    {record.operationalContext.journeyStage}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Source</p>
                  <p className="text-sm font-medium text-foreground">
                    {record.operationalContext.source}
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
        <div className="space-y-5">
          <h4 className="text-base font-semibold text-foreground sm:text-lg">
            Conduct Labels
          </h4>
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <DimensionLabelGroup
              title="Dissatisfaction"
              variant="dissatisfaction"
              groups={categoriesToGroups(record.conductLabels.complaintDetection)}
              emptyMessage="No dissatisfaction indicators"
            />
            <DimensionLabelGroup
              title="Customer Strain"
              variant="strain"
              groups={[
                { name: 'Emotional/Stress Indicators', labels: record.conductLabels.customerStrain.labels },
              ]}
              emptyMessage="No strain indicators"
            />
            <DimensionLabelGroup
              title="Foreseeable Harm"
              variant="harm"
              groups={categoriesToGroups(record.conductLabels.foreseeableHarm)}
              emptyMessage="No harm indicators"
            />
            <DimensionLabelGroup
              title="Consumer Duty"
              variant="consumer-duty"
              groups={categoriesToGroups(record.conductLabels.consumerDuty)}
              emptyMessage="No consumer duty indicators"
            />
            <DimensionLabelGroup
              title="TCF"
              variant="tcf"
              groups={categoriesToGroups(record.conductLabels.tcf)}
              emptyMessage="No TCF indicators"
            />
          </div>

          {(record.conductLabels.rootCauses.labels.length > 0 || !rootCauseClassificationEnabled) && (
            <div className="border-t border-border/70 pt-5">
              {record.conductLabels.rootCauses.labels.length > 0 ? (
                <DimensionLabelGroup
                  title="Root Causes"
                  variant="root-cause"
                  groups={groupLabelsByCategory(record.conductLabels.rootCauses.labels)}
                  emptyMessage="No root causes identified"
                />
              ) : (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Root cause classification is disabled for this organization. Historical root-cause labels remain visible on existing records.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {(complaintDetectionEnabled || vulnerabilityDetectionEnabled) && (
        <section className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-4">
            <h4 className="text-base font-semibold text-foreground sm:text-lg">
              Regulatory Flags
            </h4>

            {complaintDetectionEnabled && (
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
                <div className="flex min-h-14 items-center justify-between px-4 py-3 sm:px-5">
                  <div>
                    <h5 className="text-sm font-medium text-foreground sm:text-base">
                      Eligible Complaint Detection
                    </h5>
                  </div>
                  <button
                    onClick={() => setComplaintDetectionOpen(!complaintDetectionOpen)}
                    className="ml-2 flex size-5 flex-shrink-0 items-center justify-center"
                    aria-label={complaintDetectionOpen ? 'Collapse Complaint Analysis' : 'Expand Complaint Analysis'}
                    title={complaintDetectionOpen ? 'Collapse Complaint Analysis' : 'Expand Complaint Analysis'}
                  >
                    <IconWrapper
                      icon={complaintDetectionOpen ? FiChevronDown : FiChevronRight}
                      className="size-5 text-muted-foreground"
                    />
                  </button>
                </div>
                {complaintDetectionOpen && (
                  <div className="border-t border-border/70 bg-card px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                    {record.eligibleComplaintDetection?.heads && record.eligibleComplaintDetection.heads.length > 0 ? (
                      <FiveHeadComplaintTest
                        detection={record.eligibleComplaintDetection!}
                        isConfirmed={record.isConfirmedComplaint}
                        onConfirm={() => onConfirmComplaint?.(record.id)}
                        onReject={() => onRejectComplaint?.(record.id)}
                      />
                    ) : (
                      <div className="flex items-center justify-center py-8 text-muted-foreground">
                        <p className="text-sm">Complaint detection pending...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {vulnerabilityDetectionEnabled && (
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
                <div className="flex min-h-14 items-center justify-between px-4 py-3 sm:px-5">
                  <h5 className="text-sm font-medium text-foreground sm:text-base">
                    Vulnerability Detection
                  </h5>
                  <button
                    onClick={() => setVulnerabilityDetectionOpen(!vulnerabilityDetectionOpen)}
                    className="ml-2 flex size-5 flex-shrink-0 items-center justify-center"
                    aria-label={vulnerabilityDetectionOpen ? 'Collapse Vulnerability Detection' : 'Expand Vulnerability Detection'}
                    title={vulnerabilityDetectionOpen ? 'Collapse Vulnerability Detection' : 'Expand Vulnerability Detection'}
                  >
                    <IconWrapper
                      icon={vulnerabilityDetectionOpen ? FiChevronDown : FiChevronRight}
                      className="size-5 text-muted-foreground"
                    />
                  </button>
                </div>
                {vulnerabilityDetectionOpen && (
                  <div className="border-t border-border/70 bg-card px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                    <DimensionLabelGroup
                      title=""
                      variant="vulnerability"
                      groups={
                        record.vulnerabilityDetection?.detected
                          ? Object.entries(
                              (record.vulnerabilityDetection?.indicators ?? []).reduce<{ [key: string]: VulnerabilityIndicator[] }>((acc, indicator) => {
                                const category = formatTaxonomyLabel(indicator.category);
                                (acc[category] = acc[category] || []).push(indicator);
                                return acc;
                              }, {})
                            ).map(([category, indicators]) => ({
                              name: category,
                              labels: indicators.map((ind) => ({
                                id: ind.label,
                                rationale: ind.rationale || ind.description,
                                regulation_reference: ind.regulation_reference,
                                confidence: ind.confidence,
                              })),
                            }))
                          : []
                      }
                      emptyMessage="No vulnerability indicators detected"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {record.isConfirmedComplaint && (
        <section className="px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-4">
            <h4 className="text-base font-semibold text-foreground sm:text-lg">
              Investigation
            </h4>

            <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
              <div className="flex min-h-14 items-center justify-between px-4 py-3 sm:px-5">
                <h5 className="text-sm font-medium text-foreground sm:text-base">
                  Outcome Simulation
                </h5>
                <button
                  onClick={() => setInvestigationOpen(!investigationOpen)}
                  className="ml-2 flex size-5 flex-shrink-0 items-center justify-center"
                  aria-label={investigationOpen ? 'Collapse Outcome Simulation' : 'Expand Outcome Simulation'}
                  title={investigationOpen ? 'Collapse Outcome Simulation' : 'Expand Outcome Simulation'}
                >
                  <IconWrapper
                    icon={investigationOpen ? FiChevronDown : FiChevronRight}
                    className="size-5 text-muted-foreground"
                  />
                </button>
              </div>
              {investigationOpen && (
                <div className="space-y-4 border-t border-border/70 bg-card px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                  {record.outcomeSimulation ? (
                    <>
                      <OutcomePredictionWithConfidence
                        prediction={record.outcomeSimulation.prediction}
                        confidence={record.outcomeSimulation.confidence}
                        size="md"
                        showBar={true}
                      />
                      <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {record.outcomeSimulation.reasoning}
                      </p>
                      <div className="flex flex-col items-stretch justify-end gap-2 border-t border-border pt-4 sm:flex-row sm:items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full whitespace-nowrap sm:w-auto"
                          onClick={() => setOutcomeSimulationModalOpen(true)}
                        >
                          <IconWrapper icon={FiInfo} className="mr-2 size-4" />
                          View Full Analysis
                        </Button>
                        <Button
                          size="sm"
                          className="w-full whitespace-nowrap bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto"
                          onClick={() => onStartSimulation?.(record.id)}
                          disabled={isStartingSimulation}
                        >
                          {isStartingSimulation ? (
                            <>
                              <Loader2 className="mr-2 size-4 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <IconWrapper icon={FiCheck} className="mr-2 size-4" />
                              Start Case Guidance
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="mb-4 text-sm text-muted-foreground">
                        No case guidance yet. Start one to get the suggested FOS outcome.
                      </p>
                      <Button
                        size="sm"
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={() => onStartSimulation?.(record.id)}
                        disabled={isStartingSimulation}
                      >
                        {isStartingSimulation ? (
                          <>
                            <Loader2 className="mr-2 size-4 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <IconWrapper icon={FiCheck} className="mr-2 size-4" />
                            Start Case Guidance
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <RecordEvidenceSection initialEvidence={record.evidence || []} />

            {simulations.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
                <div className="flex min-h-14 items-center justify-between px-4 py-3 sm:px-5">
                  <h5 className="text-sm font-medium text-foreground sm:text-base">
                    Past Outcome Simulations
                  </h5>
                  <button
                    onClick={() => setPastSimulationsOpen(!pastSimulationsOpen)}
                    className="ml-2 flex size-5 flex-shrink-0 items-center justify-center"
                    aria-label={pastSimulationsOpen ? 'Collapse Past Simulations' : 'Expand Past Simulations'}
                    title={pastSimulationsOpen ? 'Collapse Past Simulations' : 'Expand Past Simulations'}
                  >
                    <IconWrapper
                      icon={pastSimulationsOpen ? FiChevronDown : FiChevronRight}
                      className="size-5 text-muted-foreground"
                    />
                  </button>
                </div>
                {pastSimulationsOpen && (
                  <div className="border-t border-border/70 bg-card px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                    {simulationsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-sm text-muted-foreground">Loading simulations...</p>
                      </div>
                    ) : (
                      <PastSimulationsTable
                        simulations={simulations}
                        onViewSimulation={(simulationId) => {
                          console.log('View simulation:', simulationId);
                          toast.info('Simulation detail view coming soon');
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Outcome Simulation Modal */}
      {record.outcomeSimulation && (
        <OutcomeSimulationModal
          open={outcomeSimulationModalOpen}
          onOpenChange={setOutcomeSimulationModalOpen}
          outcomeSimulation={record.outcomeSimulation}
        />
      )}

      {/* Classification Review Modal */}
      {record.classificationMeta?.classificationId && (
        <ClassificationReviewModal
          open={reviewModalOpen}
          onOpenChange={setReviewModalOpen}
          classificationId={record.classificationMeta.classificationId}
          record={record}
        />
      )}
    </div>
  );
};

export { RecordDetail };
